import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Breadcrumbs } from '@teamsnap/teamsnap-ui';

import { ROUTES } from '../core/routes';
import * as QueryUserOrganizations from '../core/gql/queries/user-organizations';
import * as QueryProgram from '../core/gql/queries/programs';
import * as QueryMember from '../core/gql/queries/show-member';
import * as QuerySeason from '../core/gql/queries/show-season';
import * as QueryRegistration from '../core/gql/queries/show-form';
import { TEAMSNAP_URL } from 'core/constants';
import User from '../models/user';
import { hasPermissionForOrg, Permission } from '../core/permissions';

interface Props {
  path: string;
  user: User;
}

const HubBreadcrumbs = ({ path, user }: Props) => {
  const params = useParams<any>();

  const organizationId = +params.orgId;
  const programId = +params.programId;
  const seasonId = +params.seasonId;
  const memberId = +params.memberId;
  const registrationId = +params.registrationId

  // Organization
  const queryUserOrganizations = useQuery(QueryUserOrganizations.QUERY, {
    errorPolicy: 'all',
  });

  // Program
  const queryProgram = useQuery(QueryProgram.QUERY, {
    variables: { organizationId: organizationId },
    skip: !organizationId,
    errorPolicy: 'all',
  });

  // Season
  const querySeason = useQuery(QuerySeason.QUERY, {
    variables: { seasonId: +seasonId },
    skip: !seasonId,
    errorPolicy: 'all',
  });

  const queryRegistration = useQuery(QueryRegistration.QUERY, {
    variables: { formId: registrationId, organizationId: +organizationId },
    skip: !registrationId,
    errorPolicy: 'all',
  });

  // Member
  const queryMember = useQuery(QueryMember.QUERY, {
    variables: { organizationId: organizationId, memberId: memberId },
    skip: !organizationId || !memberId,
    errorPolicy: 'all',
  });

  const isLoading = queryUserOrganizations.loading || queryProgram.loading || queryMember.loading;

  if (isLoading) {
    return null;
  }

  const breadcrumbData = {
    organization: null,
    programs: null,
    program: null,
    season: null,
    member: null,
  };

  const organization = queryUserOrganizations.data?.organizations?.find((org) => org.id === organizationId);
  if (organization || hasPermissionForOrg(user, Permission.ProgramsView, organizationId)) {
    const text = organization ? organization.name : 'Organization';
    breadcrumbData.organization = {
      text,
      link: <Link to={`/organizations/${organizationId}/programs`}>{text}</Link>,
    };

    breadcrumbData.programs = {
      text: 'Programs',
      link: <Link to={`/organizations/${organizationId}/programs`}>Programs</Link>,
    };
  }

  const program = queryProgram.data?.programs?.find((program) => program.id === programId);
  if (program) {
    const text = program.name;
    breadcrumbData.program = {
      text: program.name,
      link: <Link to={`/organizations/${organizationId}/programs/${programId}`}>{text}</Link>,
    };
  }

  const season = querySeason.data?.showSeason;
  if (season) {
    const text = season.name;
    breadcrumbData.season = {
      text: season.name,
      link: <Link to={`/organizations/${organizationId}/programs/${programId}/seasons/${seasonId}`}>{text}</Link>,
      divisionLink: <a href={`${TEAMSNAP_URL}/${season.tsDivisionId}/division_teams`}>{text}</a>,
    };
  }

  const member = queryMember.data?.member;
  if (member) {
    breadcrumbData.member = {
      text: `${member.firstName} ${member.lastName}`,
    };
  }

  const routeBreadcrumbMap = {
    [ROUTES.PROGRAM_INDEX]: [breadcrumbData.organization?.text, 'Programs'],
    [ROUTES.PROGRAM_EDIT]: [
      breadcrumbData.organization?.link,
      breadcrumbData.programs?.link,
      breadcrumbData.program?.link,
      'Edit Program',
    ],
    [ROUTES.PROGRAM_SEASONS]: [
      breadcrumbData.organization?.link,
      breadcrumbData.programs?.link,
      breadcrumbData.program?.text,
    ],
    [ROUTES.PROGRAM_NEW]: [
      breadcrumbData.organization?.link,
      breadcrumbData.programs?.link,
      'New Program',
    ],
    [ROUTES.PROGRAM_REPORT]: [
      breadcrumbData.organization?.link,
      breadcrumbData.programs?.link,
      'Programs Report',
    ],
    [ROUTES.MEMBER_INDEX]: [breadcrumbData.organization?.link, 'Members'],
    [ROUTES.FINANCIALS]: [breadcrumbData.organization?.link, 'Financials'],
    [ROUTES.ORDER_DETAILS]: [
      breadcrumbData.organization?.link,
      <Link to={`/organizations/${organizationId}/financials`}>
        Financials
      </Link>,
      'Order',
    ],
    [ROUTES.MEMBER]: [
      breadcrumbData.organization?.link,
      <Link to={`/organizations/${organizationId}/members`}>Members</Link>,
      breadcrumbData.member?.text,
    ],
    [ROUTES.MEMBER_NEW]: [
      breadcrumbData.organization?.link,
      <Link to={`/organizations/${organizationId}/members`}>Members</Link>,
      'New Member',
    ],
    [ROUTES.MEMBER_BACKGROUND_CHECK_NEW]: [
      breadcrumbData.organization?.link,
      <Link to={`/organizations/${organizationId}/members`}>Members</Link>,
      'Background Checks',
    ],
    [ROUTES.STAFF_INDEX]: [breadcrumbData.organization?.link, 'Staff'],
    [ROUTES.STAFF_EDIT]: [
      breadcrumbData.organization?.link,
      <Link to={`/organizations/${organizationId}/staff`}>Staff</Link>,
      'Edit Staff Member',
    ],
    [ROUTES.STAFF_NEW]: [
      breadcrumbData.organization?.link,
      <Link to={`/organizations/${organizationId}/staff`}>Staff</Link>,
      'Invite Staff Member',
    ],
    [ROUTES.STAFF_BACKGROUND_CHECK_NEW]: [
      breadcrumbData.organization?.link,
      <Link to={`/organizations/${organizationId}/staff`}>Staff</Link>,
      'Background Checks',
    ],
    [ROUTES.MESSAGE_NEW]: [
      breadcrumbData.organization?.link,
      <Link to={`/organizations/${organizationId}/messages`}>Messages</Link>,
      'New Message',
    ],
    [ROUTES.MESSAGE_INDEX]: [breadcrumbData.organization?.link, 'Messages'],
    [ROUTES.MESSAGE_SENT]: [breadcrumbData.organization?.link, 'Messages'],
    [ROUTES.SETTINGS_INDEX]: [breadcrumbData.organization?.link, 'Settings'],
    [ROUTES.INVOICES_INDEX]: [breadcrumbData.organization?.link, 'Invoices'],
    [ROUTES.REGISTRATION_INDEX]: [
      breadcrumbData.organization?.link,
      'Registration',
    ],
    [ROUTES.REGISTRATION_DETAILS]: [
      breadcrumbData.organization?.link,
      <Link to={`/organizations/${organizationId}/registration`}>
        Registration
      </Link>,
      queryRegistration.data?.showRegistrationForm?.name,
    ],
    [ROUTES.PARTICIPANTS_INDEX]: [
      <Link to={`/organizations/${organizationId}/programs`}>
        Organization
      </Link>,
      breadcrumbData.program?.link,
      breadcrumbData.season?.divisionLink,
      'Participants',
    ],
    [ROUTES.PARTICIPANT]: [
      <Link to={`/organizations/${organizationId}/programs`}>
        Organization
      </Link>,
      breadcrumbData.program?.link,
      breadcrumbData.season?.divisionLink,
      <Link
        to={`/organizations/${organizationId}/programs/${programId}/seasons/${seasonId}/participants`}
      >
        Participants
      </Link>,
      breadcrumbData.member?.text,
    ],
    [ROUTES.HISTORICAL_SEASONS]: [
      breadcrumbData.organization?.text,
      breadcrumbData.programs?.link,
      'Historical Seasons',
    ],
    [ROUTES.WEBSITES]: [breadcrumbData.organization?.link, 'Websites'],
    [ROUTES.COACHES_PACK]: [breadcrumbData.organization?.link, 'Coaches Pack'],
    [ROUTES.SPONSORSHIPS]: [breadcrumbData.organization?.link, 'Sponsorships'],
    [ROUTES.TOURNAMENTS]: [breadcrumbData.organization?.link, 'Tournaments'],
    [ROUTES.SEASON_REGISTRATIONS_INDEX]: [
      <Link to={`/organizations/${organizationId}/programs`}>
        Organization
      </Link>,
      breadcrumbData.program?.link,
      breadcrumbData.season?.divisionLink,
      'Registration',
    ],
    [ROUTES.CALENDAR]: [breadcrumbData.organization?.link, 'Calendar'],
    [ROUTES.SEASON_FINANCIALS]: [
      <Link to={`/organizations/${organizationId}/programs`}>
        Organization
      </Link>,
      breadcrumbData.program?.link,
      breadcrumbData.season?.divisionLink,
      'Financials',
    ],
  };

  const routeBreadcrumbs = routeBreadcrumbMap[path];

  return <>{!!routeBreadcrumbs && <Breadcrumbs breadcrumbs={routeBreadcrumbs} />}</>;
};

export default HubBreadcrumbs;
